import { useMutation, useQueryClient } from '@tanstack/react-query';
import { canInAnyScope } from '@testflow/shared';
import { useTranslation } from 'react-i18next';
import { CatalogTable } from '../../components/catalog-table.js';
import { type Step, catalogApi } from '../../lib/catalog-api.js';
import { useScopedUser } from '../../lib/project-roles.js';
import { useUrlState } from '../../lib/use-url-state.js';
import {
  type StepDraft,
  type StepLanguageMode,
  emptyStepDraft,
  stepColumns,
  toStepDraft,
} from './step-columns.js';

// #28: Die Schritte sind die fuenfte Tabelle im #52-Muster. Anlegen ist damit keine eigene
// Codestelle mehr, sondern eine leere Bearbeiten-Zeile — Anlegen und Bearbeiten koennen nicht
// mehr auseinanderdriften (Testrunde-1-Finding 3). Die Reihenfolge-Knoepfe haengen an
// `rowActions` und sind deshalb waehrend einer offenen Zeile nicht da.

// #81b: gueltige Werte des URL-Parameters `step_lang` — schuetzt gegen ein manipuliertes
// `?step_lang=xyz`, das sonst als String durchgereicht wuerde statt auf eine sinnvolle Vorgabe
// zurueckzufallen (gleiches Muster wie `view` in board-page.tsx).
const STEP_LANGUAGE_MODES: StepLanguageMode[] = ['de', 'en', 'both'];

export function StepTable({
  projectId,
  testCaseId,
  steps,
}: {
  projectId: string;
  testCaseId: string;
  steps: Step[];
}) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useScopedUser(projectId);
  const canEdit = user ? canInAnyScope(user, 'catalog.edit') : false;

  // #81b: DE/EN/Beide — vorbelegt mit der aktiven UI-Sprache (dieselbe Normalisierung wie
  // kindLabel in data-kinds-api.ts: alles ausser 'en' faellt auf 'de' zurueck, passend zu
  // fallbackLng 'de' in lib/i18n.ts), lebt danach in der URL (Muster #36/#78: deep-linkbar,
  // reload-fest). Ein manipulierter/veralteter Param faellt auf die Vorgabe zurueck statt eine
  // leere Tabelle zu zeigen.
  const defaultStepLang: StepLanguageMode = i18n.language === 'en' ? 'en' : 'de';
  const [stepLangParam, setStepLangParam] = useUrlState('step_lang', defaultStepLang);
  const stepLang: StepLanguageMode = STEP_LANGUAGE_MODES.includes(stepLangParam as StepLanguageMode)
    ? (stepLangParam as StepLanguageMode)
    : defaultStepLang;

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['tc', projectId, testCaseId] });
    // #143: Schritte bestimmen die Übersetzungslücke des Testfalls — die Volliste des gefilterten
    // Baums (Filter „fehlende Übersetzung") muss das mitbekommen.
    void queryClient.invalidateQueries({ queryKey: ['tcs-search', projectId] });
    // #153: Die Zuordnungsseite zeigt die Schrittzahl in ihrer Testfallliste neben dieser Tabelle.
    void queryClient.invalidateQueries({ queryKey: ['catalog-testcases', projectId] });
  };

  const addStep = useMutation({
    mutationFn: (d: StepDraft) => catalogApi.addStep(projectId, testCaseId, d),
    meta: { successToast: 'feedback.created', errorToast: false },
    onSuccess: invalidate,
  });
  const updateStep = useMutation({
    mutationFn: ({ stepId, d }: { stepId: string; d: Partial<StepDraft> }) =>
      catalogApi.updateStep(projectId, stepId, d),
    meta: { errorToast: false },
    onSuccess: invalidate,
  });
  const deleteStep = useMutation({
    mutationFn: (stepId: string) => catalogApi.deleteStep(projectId, stepId),
    meta: { successToast: 'feedback.deleted', errorToast: false },
    onSuccess: invalidate,
  });
  const reorderSteps = useMutation({
    mutationFn: (stepIds: string[]) => catalogApi.reorderSteps(projectId, testCaseId, stepIds),
    meta: { errorToast: false },
    onSuccess: invalidate,
  });

  const ordered = steps.slice().sort((a, b) => a.step_no - b.step_no);

  // Vertauscht zwei Schritte und schickt die volle neue Reihenfolge — der Server vergibt daraus
  // die step_no-Werte (keine lokale Nummerierung noetig).
  const move = (stepId: string, direction: -1 | 1) => {
    const index = ordered.findIndex((s) => s.step_id === stepId);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= ordered.length) return;
    const reordered = ordered.slice();
    const [moved] = reordered.splice(index, 1);
    if (!moved) return;
    reordered.splice(target, 0, moved);
    reorderSteps.mutate(reordered.map((s) => s.step_id));
  };

  return (
    <div className="rounded-card border border-[var(--border)] bg-bg-surface p-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <span className="text-[11px] font-bold uppercase tracking-wide text-text-muted">
          {t('catalog.detail.steps')}
        </span>
        {/* #81b: DE/EN/Beide — reduziert bei 1280px die Schritt-Tabelle auf acht statt zehn
            Spalten, ohne DE/EN-Inhalte wegzuwerfen (die bleiben ueber die Draft-Felder erhalten,
            der Modus filtert nur die Anzeige). C1-Fix: description_de/description_en zaehlen seit
            dem Pflichtfeld-Fix in JEDEM Modus mit (acht statt sieben) — ein Ein-Sprach-Nutzer
            braucht sonst kein Bedienelement fuer das Pflichtfeld seiner eigenen Sprache. */}
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] text-text-muted">
            {t('catalog.detail.stepLanguage.label')}
          </span>
          <div className="flex overflow-hidden rounded-chip border border-[var(--border)] text-xs">
            {STEP_LANGUAGE_MODES.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setStepLangParam(m)}
                aria-pressed={stepLang === m}
                className={`px-2 py-1 ${
                  stepLang === m ? 'bg-brand-accent text-white' : 'text-text-muted'
                }`}
              >
                {t(`catalog.detail.stepLanguage.${m}`)}
              </button>
            ))}
          </div>
        </div>
      </div>
      <CatalogTable
        columns={stepColumns((k, o) => (o ? t(k, o) : t(k)), stepLang)}
        rows={ordered}
        rowKey={(r) => r.step_id}
        toDraft={toStepDraft}
        emptyDraft={emptyStepDraft}
        onCreate={(d) => addStep.mutateAsync(d)}
        onUpdate={(stepId, d) => {
          // Nur die tatsaechlich geaenderten Felder schicken: der Server loescht das
          // *_en_ai-Abzeichen schon, wenn der Key ueberhaupt im Body steht
          // (catalog-steps.ts). Ein Voll-Draft wuerde also bei jeder Korrektur am T-Code
          // beide Uebersetzungs-Abzeichen des Schritts loeschen - unwiederbringlich, denn
          // gesetzt werden sie nur vom Uebersetzungslauf.
          const row = ordered.find((s) => s.step_id === stepId);
          const before = row ? toStepDraft(row) : emptyStepDraft;
          const patch = Object.fromEntries(
            (Object.keys(d) as (keyof StepDraft)[])
              .filter((k) => d[k] !== before[k])
              .map((k) => [k, d[k]]),
          );
          return updateStep.mutateAsync({ stepId, d: patch });
        }}
        onDelete={(stepId) => deleteStep.mutateAsync(stepId)}
        deleteLabel={(r) =>
          `${t('catalog.fields.stepNo')}${r.step_no}: ${r.description_de.slice(0, 40)}`
        }
        canEdit={canEdit}
        addLabel={t('catalog.actions.addStep')}
        rowActions={(r) => {
          const index = ordered.findIndex((s) => s.step_id === r.step_id);
          return (
            <>
              <button
                type="button"
                onClick={() => move(r.step_id, -1)}
                disabled={index === 0 || reorderSteps.isPending}
                className="rounded-btn px-1 text-xs text-text-muted hover:text-text-body disabled:opacity-30"
                aria-label={t('catalog.actions.moveUp')}
                title={t('catalog.actions.moveUp')}
              >
                ▲
              </button>{' '}
              <button
                type="button"
                onClick={() => move(r.step_id, 1)}
                disabled={index === ordered.length - 1 || reorderSteps.isPending}
                className="rounded-btn px-1 text-xs text-text-muted hover:text-text-body disabled:opacity-30"
                aria-label={t('catalog.actions.moveDown')}
                title={t('catalog.actions.moveDown')}
              >
                ▼
              </button>
            </>
          );
        }}
      />
    </div>
  );
}
